import { makeAutoObservable } from 'mobx';
import {DomainStore} from 'shared/store';
import {t} from 'shared/core';
import {auth, endpoints, types} from 'main/tools';
import _ from 'lodash';
import {PerformanceReviewReview, PerformanceReviewReviewee} from 'main/stores/performance_reviews';
import {successAlert} from 'shared/tools';
import {setupAutosaveDraft} from 'main/tools';

class RevieweeResultsState {
  store = new DomainStore();

  goalTimePeriodOptions = [
    { id: 'past', status: 'past', display: t('goals.Past') },
    { id: 'current', status: 'current', display: t('goals.Current') },
    { id: 'upcoming', status: 'upcoming', display: t('goals.Upcoming') }
  ];

  reviews = [];
  reviewee;
  match;
  reviewAnswerRefs = [];
  errors = {};
  submitReviewModalOpen = false;

  constructor() {
    makeAutoObservable(this, {
      store: false
    });
  }

  receiveProps({match}) {
    this.match = match;
  }

  async load() {
    const {params} = this.match;
    await this.store._compose([
      endpoints.PERFORMANCE_REVIEWS.REVIEWS.FOR_REVIEWEE.with(params.revieweeId),
      endpoints.PERFORMANCE_REVIEWS.REVIEWEE.with(params.revieweeId)
    ]);

    this.reviews = this.store._getAll(types.PERFORMANCE_REVIEWS.REVIEW, PerformanceReviewReview);
    this.reviewee = new PerformanceReviewReviewee(this.store._getSingle(types.PERFORMANCE_REVIEWS.REVIEWEE, { id: params.revieweeId }));

    this.reviews = await Promise.all(this.reviews.map(async (review) => {
      if (review.reviewer.id !== auth.employee.id) return review;

      if (!review.readOnly) {
        const questions = _.map(review.answers, 'question');

        await setupAutosaveDraft(
          review,
          {id: review.id, type: types.PERFORMANCE_REVIEWS.REVIEW, omittedAttributes: ['reviewee.employee', 'type', 'notes']}
        );
        review.regenerateAnswersForQuestions(questions);
      }

      return review;
    }));
  }

  openSubmitReviewModal() {
    this.submitReviewModalOpen = true;
  }

  closeSubmitReviewModal() {
    this.submitReviewModalOpen = false;
  }

  goToGoal(goal) {
    window.open(`/goals/view/${goal.id}`, '_blank');
  }

  async submitReview(review) {
    const {model, errors} = await this.store.patch(
      review.link('submit'),
      types.PERFORMANCE_REVIEWS.REVIEW,
      review
    );
    this.errors = errors;

    if (model) {
      review.update(model);
      successAlert(t('performance_reviews.view.alerts.Review submitted.'));
    }

    this.closeSubmitReviewModal();
  }

  async unsubmitReview(review) {
    const {model, errors} = await this.store.patch(
      review.link('unsubmit'),
      types.PERFORMANCE_REVIEWS.REVIEW,
      review
    );
    this.errors = errors;

    if (model) {
      if (review.autosaver) review.autosaver.clearAutosaver();
      review.update(model);
      await setupAutosaveDraft(
        review,
        {id: review.id, type: types.PERFORMANCE_REVIEWS.REVIEW}
      );
    }
  }

  async shareReview(review) {
    const {model} = await this.store.post(
      review.link('share'),
      types.PERFORMANCE_REVIEWS.REVIEW
    );

    if (model) {
      review.update(model);
      successAlert(t('performance_reviews.view.alerts.Review shared.'));
    }
  }

  async unshareReview(review) {
    const {model} = await this.store.post(
      review.link('unshare'),
      types.PERFORMANCE_REVIEWS.REVIEW
    );

    if (model) {
      review.update(model);
      successAlert(t('performance_reviews.view.alerts.Review unshared.'));
    }
  }

  onAnswerChanged(review) {
    review.autosaver.autosave();
  }

  get backButtonDetails() {
    const BASE_TITLE = 'performance_reviews.view.back_paths';
    const {backPath} = this.match.params;

    if (backPath === 'employee') {
      return {
        backPath: `/employees/${this.reviewee.employee.id}/performance`,
        backTitle: this.reviewee.employee.name
      };
    } else if (backPath === 'reviewees') {
      return {
        backPath: '/reviews/reviewees',
        backTitle: t(`${BASE_TITLE}.${backPath}`)
      };
    } else if (['write', 'admin'].includes(backPath)) {
      return {
        backPath: `/reviews/reviews/${this.reviewee.cycle.id}/${backPath}`,
        backTitle: t(`${BASE_TITLE}.${backPath}`)
      };
    } else {
      return {
        backPath: '/reviews',
        backTitle: t(`${BASE_TITLE}.back`)
      };
    }
  }

  get canViewRevieweeGoals() {
    return this.hasViewableReviews &&
             (this.reviewee.employee.id === auth.employee.id ||
               !!this.reviewee.employee.accessLevel('::PERFORMANCE_GOALS') ||
               auth.hasAccess('::MANAGE_PERFORMANCE_MANAGEMENT'));
  }

  get hasReviews() {
    if (_.isEmpty(this.reviews)) return false;

    return !(_.isEmpty(this.reviewsWithAnswers) && _.isEmpty(this.writableReviews));
  }

  get reviewsWithAnswers() {
    return _.filter(this.otherReviewerReviews, (review) => {
      return !_.isEmpty(review.answers) && review.submitted;
    });
  }

  get sortedReviews() {
    const reviewsWithoutAnswers = _.differenceBy(
      this.otherReviewerReviews,
      this.reviewsWithAnswers,
      'id'
    );

    return [...this.writableReviews, ...this.reviewsWithAnswers, ...reviewsWithoutAnswers];
  }

  get hasOtherReviewerReviews() {
    return !_.isEmpty(this.otherReviewerReviews);
  }

  get hasViewableReviews() {
    return !_.isEmpty(this.viewableReviews);
  }

  get otherReviewerReviews() {
    return _.reject(this.reviews, {reviewer: { id: auth.employee.id }});
  }

  get writableReviews() {
    return _.filter(this.reviews, {reviewer: { id: auth.employee.id }, reviewee: { scheduled: false }});
  }

  get viewableReviews() {
    return [...this.writableReviews, ...this.reviewsWithAnswers];
  }

  visibilityNotice(review) {
    if (review.reviewer.id !== auth.employee.id) return;

    const kind = _.get(review, 'type.kind');
    switch (kind) {
      case 'self':
        return 'performance_reviews.view.SELF_VISIBILITY_NOTICE';
      case 'peer':
        return 'performance_reviews.view.PEER_VISIBILITY_NOTICE';
      case 'manager':
        return 'performance_reviews.view.MANAGER_VISIBILITY_NOTICE';
    }
  }

  reviewDetailsTitle(review) {
    if (review.reviewer.id === auth.employee.id) return 'performance_reviews.view.My Feedback';

    return 'performance_reviews.view.REVIEWER_FEEDBACK';
  }

  scrollToReview(model) {
    const review = _.find(this.viewableReviews, { id: model.id });
    if (!review) return;

    const ref = _.find(this.reviewAnswerRefs, ref => ref.id === review.id);

    if (ref) ref.element.scrollIntoView({ behavior: 'smooth' });
  }

  addRef(ref) {
    if (!ref) return;

    const elementWithId = {
      id: ref.id,
      element: ref
    };

    this.reviewAnswerRefs.push(elementWithId);
  }
}

export default RevieweeResultsState;
